import { FC } from "react";
import * as _ from "lodash";

import { Typography, Row, Col, Skeleton, Alert } from "@ctra/components";
import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Effect, TrackerEntity } from "@ctra/api";

import { useEvent } from "@events";
import { QnAContext } from "@ask-ida";

import { KPIChange } from "./components";
import styles from "./IdaQnAEventPage.module.less";

const { Title, Paragraph } = Typography;

/**
 * Page to show QnA for an event
 * @return {JSX.Element}
 * @constructor
 */
export const IdaQnAEventPage: FC = () => {
  const { t } = useTranslation();

  const {
    events: {
      page: { title, description }
    },
    askIda: { noData }
  } = Content;

  const {
    event: {
      source: { type, id: sourceId }
    }
  } = useEvent();

  return (
    <QnAContext.Provider id={+sourceId}>
      <QnAContext.Consumer>
        {({ trackerList, meta: { isLoading, isArchived } }) => {
          if (!_.isEmpty(trackerList)) {
            const {
              answer: { results },
              question: { effects }
            } = _.first(_.values(trackerList)) as TrackerEntity;

            /**
             * Make a list of legacy effects
             * @type {Effect[]}
             */
            const effectList: Array<Effect> = _.compact(_.map(effects, "effect"));

            /**
             * Make an intersection between the results and the effects
             * to check if there is anything to display
             */
            const targetOfInterest = _.intersection<Effect>(_.keys(results) as Array<Effect>, effectList);

            /**
             * Sort effects: increase, decrease, insignificant
             * @type {string[]}
             */
            const sortedTargets = _.sortBy(targetOfInterest, [
              (effect) => !results[effect].significant,
              (effect) => {
                const expected = results[effect].expectedAfter;
                const value = results[effect].valueAfter;

                return expected ? expected - value : -results[effect].valueChangePercentage;
              }
            ]);

            if (sortedTargets.length > 0) {
              return (
                <section className={styles.PageWrapper}>
                  <Title level={6} theme="black">
                    {t<string>(title, { type })}
                  </Title>
                  <Paragraph>{t<string>(description, { type })}</Paragraph>
                  <Row gutter={[16, 16]}>
                    {_.map(effectList, (effect) => (
                      <Col md={12} span={24}>
                        <KPIChange key={effect} effect={effect} values={results[effect]} />
                      </Col>
                    ))}
                  </Row>
                </section>
              );
            }
          }

          if (isLoading) {
            return <Skeleton className={styles.Margin} active={isLoading} loading={isLoading} />;
          }

          return (
            <Col className={styles.Margin}>
              <Alert type="info" showIcon message={t(noData.message)} description={t(noData.description)} />
            </Col>
          );
        }}
      </QnAContext.Consumer>
    </QnAContext.Provider>
  );
};
